import { ResizeEvent } from 'angular-resizable-element';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  OnDestroy,
  EventEmitter,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { SnAbilityService } from '@lib/ability';
import { SnCamera, SnCameraManagementType } from '@lib/api/entities';
import { ChannelsToShareDialogComponent } from '@lib/communication/twiliochat/components';
import { SnChannel } from '@lib/communication/twiliochat/models';
import { SnMessagesService } from '@lib/communication/twiliochat/services';

import { SnAuditCameraStreamViewContainer } from '../../containers/audit-camera-stream-view';

@Component({
  selector: 'sn-cam-feed-card',
  templateUrl: './cam-feed-card.component.html',
  styleUrls: ['./cam-feed-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CamFeedCardComponent implements OnInit, OnDestroy {
  @Input() public camera: SnCamera;
  @Input() public unlockButton: boolean = true;
  @Input() public dragControls: boolean = true;
  @Input() public allowPlayback: boolean = true;
  @Input() public unlockedCamList: any;
  @Input() public playback: boolean = true;
  @Input() public unlockDisabled: boolean = false;
  @Input() public playbackDisabled: boolean = false;
  @Input() public actions: boolean;
  @Input() public dragDisabled: boolean;
  @Input() public alwaysOn: boolean;

  @ViewChild('auditCameraStream') public audit: SnAuditCameraStreamViewContainer;

  public readonly canChange: boolean;

  public style: any = {};

  @Output()
  public readonly unlockCamera = new EventEmitter<any>();

  @Output()
  public readonly lockCamera = new EventEmitter<any>();

  @Output()
  public readonly viewPlayback = new EventEmitter<SnCamera>();

  @Output()
  public readonly removePlayback = new EventEmitter<SnCamera>();

  @Output()
  public readonly undockCamera = new EventEmitter<SnCamera>();

  @Output()
  public readonly drawPolyline = new EventEmitter<{ camera: SnCamera; element: Element }>();

  @Output()
  public readonly removePolyline = new EventEmitter<void>();

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _chatMessageService: SnMessagesService,
    private readonly _ability: SnAbilityService,
    private readonly _dialog: MatDialog,
    public readonly _toast: ToastrService,
  ) {
    this.canChange = this._ability.can({ subject: 'cameras/camera', action: 'change' });
  }

  get isUnmanaged(): boolean {
    return this.camera.managementType === SnCameraManagementType.Unmanaged;
  }

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public unLock(): void {
    this.unlockCamera.emit(this.camera);
  }

  public lockCam(): void {
    this.lockCamera.emit(this.camera);
  }

  public viewCamPlayback(): void {
    this.viewPlayback.next(this.camera);
  }

  public trackCam(index: Number, item: SnCamera): string {
    return item.id;
  }

  public onResizeEnd(event: ResizeEvent): void {
    this.style = {
      left: `${event.rectangle.left}px`,
      top: `${event.rectangle.top}px`,
      width: `${event.rectangle.width}px`,
    };
    this._changeDetectorRef.detectChanges();
  }

  public validate(event: ResizeEvent): boolean {
    const MIN_DIMENSIONS_PX: number = 50;
    if (
      event.rectangle.width &&
      event.rectangle.height &&
      (event.rectangle.width < MIN_DIMENSIONS_PX || event.rectangle.height < MIN_DIMENSIONS_PX)
    ) {
      return false;
    }
    return true;
  }

  public removeCamera(): void {
    this.undockCamera.next(this.camera);
    this.removePlayback.next(this.camera);
  }

  public detect(): void {
    setTimeout(() => this._changeDetectorRef.detectChanges(), 0);
  }

  public onMouseOver(): void {
    this.detect();

    const element: Element = document.getElementById('camera-' + this.camera.id);
    if (element) {
      this.drawPolyline.next({ camera: this.camera, element });
    }
  }

  public onMouseOut(): void {
    this.detect();
    this.removePolyline.next();
  }

  public toggleFullscreen(): void {
    this.audit.toggleFullscreen();
  }

  public openChannel(): void {
    const dialogRef = this._dialog.open(ChannelsToShareDialogComponent);

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((channel) => {
        if (channel?.twilioChannel) {
          this._shareTo(channel);
        }
      });
  }

  private _shareTo(channel: SnChannel): void {
    this._chatMessageService
      .shareMediaMessage(channel, this.camera.name, {
        type: 'camera',
        cameraId: this.camera.id,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._toast.success('Camera Shared');
      });
  }
}
